import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { AuthService } from './auth.service';

/** P1/P2 UX flag names seeded by the backend (disabled until gradual rollout). */
export const UX_FEATURE_FLAGS = {
  collapsedNav: 'ux_collapsed_nav',
  unifiedUpload: 'ux_unified_upload',
  playgroundSuccessRitual: 'ux_playground_success_ritual',
  templatePrebuiltDefault: 'ux_template_prebuilt_default',
  emptyStateStandard: 'ux_empty_state_standard',
  personaFirstRun: 'ux_persona_first_run',
  examplesGallery: 'ux_examples_gallery',
  dashboardCelebrations: 'ux_dashboard_celebrations',
} as const;

export type UxFeatureFlagName = (typeof UX_FEATURE_FLAGS)[keyof typeof UX_FEATURE_FLAGS];

interface MyFeatureFlagsResponse {
  flags: Record<string, boolean>;
}

/**
 * Customer feature-flag bootstrap with in-memory cache.
 * Later P1 features call `isEnabled('ux_...')` after flags load.
 */
@Injectable({
  providedIn: 'root'
})
export class FeatureFlagService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);

  private readonly flagsSubject = new BehaviorSubject<Record<string, boolean>>({});
  readonly flags$ = this.flagsSubject.asObservable();

  private loaded = false;
  private loadInFlight: Observable<Record<string, boolean>> | null = null;

  constructor() {
    this.auth.currentUser$.subscribe(user => {
      if (user) {
        this.load().subscribe();
      } else {
        this.clearCache();
      }
    });
  }

  /**
   * Fetch and cache evaluated flags for the current user.
   * Concurrent callers share one in-flight request.
   */
  load(force = false): Observable<Record<string, boolean>> {
    if (this.loaded && !force) {
      return of(this.flagsSubject.value);
    }
    if (this.loadInFlight && !force) {
      return this.loadInFlight;
    }

    this.loadInFlight = this.http
      .get<MyFeatureFlagsResponse>(`${environment.apiUrl}/feature-flags/me`)
      .pipe(
        map(response => response.flags ?? {}),
        tap(flags => {
          this.flagsSubject.next(flags);
          this.loaded = true;
          this.loadInFlight = null;
        }),
        catchError(error => {
          console.error('[FeatureFlagService] Failed to load flags:', error);
          this.loadInFlight = null;
          // Keep prior cache; treat as all-off only if never loaded
          if (!this.loaded) {
            this.flagsSubject.next({});
          }
          return of(this.flagsSubject.value);
        }),
        shareReplay(1)
      );

    return this.loadInFlight;
  }

  /** Ensure flags are loaded; completes when cache is ready. */
  ensureLoaded(): Observable<Record<string, boolean>> {
    return this.load();
  }

  /**
   * Sync check against cache. Unknown / not-yet-loaded flags are false
   * (safe default for gradual rollout).
   */
  isEnabled(flagName: string): boolean {
    return !!this.flagsSubject.value[flagName];
  }

  /** Observable of enabled state after ensuring flags are loaded. */
  isEnabled$(flagName: string): Observable<boolean> {
    return this.load().pipe(map(flags => !!flags[flagName]));
  }

  clearCache(): void {
    this.flagsSubject.next({});
    this.loaded = false;
    this.loadInFlight = null;
  }

  get snapshot(): Record<string, boolean> {
    return this.flagsSubject.value;
  }
}
